import { AnalysisMetricTypes, BaseRateTypes, DealFinancialMetricType, Guid } from '../models';
import { FormikHelpers } from 'formik';

export const ApiRootUrl = process.env.REACT_APP_CEREBRO_API_ROOT_URL ?? '';

export function SetFieldErrors(
	apiErrors: ApiErrors,
	setFieldError: FormikHelpers<unknown>['setFieldError'],
	convertToCamelCase: boolean = false
): void {
	for (var field in apiErrors) {
		const fieldName = convertToCamelCase ? field[0].toLowerCase() + field.substring(1) : field;
		setFieldError(fieldName, apiErrors[field][0]);
	}
}

export interface ApiErrors {
	[key: string]: string[];
}

export interface CerebroNotFound {
	/**
	 * The name of the object that was not found.
	 */
	objectNotFound: string;

	/**
	 * A message explaining why the object was not found.
	 */
	message: string;
}

export function IsCerebroNotFound(value: any): value is CerebroNotFound {
	return value?.objectNotFound !== undefined && value?.message !== undefined;
}

export const CerebroApiEndpoints = {
	Users: {
		Current: `/Api/Users/Current`,
		TransactionManagers: (userGuid: Guid) => `/Api/Users/GetTransactionManagers/${userGuid}`,
		LenderRelationsManagers: (userGuid: Guid) => `/Api/Users/GetLenderRelationsManagers/${userGuid}`,
		SalesReps: (userGuid: Guid) => `/Api/Users/GetSalesReps/${userGuid}`,
	},

	Deals: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}`,
		Related: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Related`,
		RelatedDownload: (dealGuid: Guid | undefined) => `${ApiRootUrl}/Api/Deals/${dealGuid}/Related/Download`,
		RelatedDealsReport: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Related/Report`,
		Invoices: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Invoices`,
		Nda: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Nda`,
		UploadNda: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Nda/Upload`,
		AssociateNda: (dealGuid: Guid | undefined, ndaGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Nda/${ndaGuid}/Associate`,
		DownloadNda: (dealGuid: Guid | undefined, ndaGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Nda/${ndaGuid}/Download`,
		AbsoluteDownloadNda: (dealGuid: Guid | undefined, ndaGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/Nda/${ndaGuid}/Download`,
		AbsoluteInvoiceDownload: (invoiceGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Invoices/${invoiceGuid}/Download`,
		Subscriptions: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Subscriptions`,
		CompanySubscriptions: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/CreateCompanySubscription`,
		SpecificSubscription: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}`,
		GenerateDealSummary: (dealGuid: Guid | undefined) => `${ApiRootUrl}/Api/Deals/${dealGuid}/GenerateDealSummary`,
		SuccessStory: (dealGuid: Guid | undefined) => `${ApiRootUrl}/Api/Deals/${dealGuid}/SuccessStory`,
		SpecificSubscriptionDownload: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Download`,
		CompanySubscriptionContractDownload: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/CompanySubscriptions/${subscriptionGuid}/Contract/Download`,
		SubscriptionContractDownload: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Contract/Download`,
		SubscriptionContractUpload: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/UploadContract`,
		SendContractForSigning: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Contract/SendForSigning`,
		UpdateContractReminder: (
			dealGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			agreementId: string | undefined
		) => `${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Agreement/${agreementId}/Reminders`,
		AgreementStatus: (
			dealGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			agreementId: string | undefined
		) => `${ApiRootUrl}/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Contract/${agreementId}/status`,
		ChangeSubscriptionProduct: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/ChangeProduct`,
		UploadSubscriptionAgreement: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/Agreement`,
		AssociateSubscription: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/AssociateCompanySubscription`,
		DealStatus: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/UpdateDealStatus`,
		AddFundingLender: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/FundingLenders`,
		SpecificFundingLender: (dealGuid: Guid | undefined, fundingLenderGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/FundingLenders/${fundingLenderGuid}`,
		UpdateClosedFunded: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/UpdateClosedFunded`,
		SuccessFeeTiers: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/SuccessFeeTiers`,
		SepcificSuccessFeeTiers: (
			dealGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			tierGuid: Guid | undefined
		) => `/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/SuccessFeeTiers/${tierGuid}`,
		PaymentTerms: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/PaymentTerms`,
		SpecificPaymentTerm: (
			dealGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			paymentTermGuid: Guid | undefined
		) => `/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/PaymentTerms/${paymentTermGuid}`,
		CreateSuccessFeeInvoice: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Subscriptions/${subscriptionGuid}/CreateSuccessFeeInvoice`,
	},
	DataRooms: {
		FolderHierarchy: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Folders/Hierarchy`,
	},
	Allocation: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Allocation`,
	LoanOptions: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Allocation/LoanOptions`,
	StatusHistory: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/StatusHistory`,

	AllocationRealEstates: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Allocation/RealEstates`,
		Specific: (dealGuid: Guid | undefined, realEstateGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/Allocation/RealEstates/${realEstateGuid}`,
	},

	DealPartners: {
		Search: (search?: string | null) => (search == null ? `Api/DealPartners` : `Api/DealPartners?search=${search}`),
		Contacts: (dealPartnerGuid: Guid | undefined) => `Api/DealPartners/${dealPartnerGuid}/Contacts`,
	},

	DebtSchedules: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/DebtSchedules`,
		Specific: (dealGuid: Guid | undefined, debtScheduleGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/DebtSchedules/${debtScheduleGuid}`,
	},

	ExcludedLenders: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ExcludedLenders`,
		Specific: (dealGuid: Guid | undefined, excludedLenderGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/ExcludedLenders/${excludedLenderGuid}`,
	},

	ReducedFeeLenders: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ReducedFeeLenders`,
		Specific: (dealGuid: Guid | undefined, lenderGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/ReducedFeeLenders/${lenderGuid}`,
	},

	Metrics: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Metrics`,

		FilteredList: (
			dealGuid: Guid | undefined,
			analysisMetricType?: AnalysisMetricTypes,
			metrics?: DealFinancialMetricType[]
		) => {
			let url = `/Api/Deals/${dealGuid}/Metrics?`;

			if (analysisMetricType !== null) url += `&analysisMetricType=${analysisMetricType}`;

			let metricTypes = '';
			if (metrics && metrics.length > 0) {
				metricTypes = toQueryParams('metricTypes', metrics);
				url += metricTypes;
			}

			return [url, metricTypes] as const;
		},
	},

	DealGeographies: (dealGuid: Guid | undefined, metricType: AnalysisMetricTypes) =>
		`/Api/Deals/${dealGuid}/Geographies?metricType=${metricType}`,

	RequiredDocuments: {
		Root: (dealGuid: Guid | undefined) => `Api/Deals/${dealGuid}/RequiredDocuments`,
		Specific: (dealGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}`,
		Templates: () => `Api/Deals/RequiredDocuments/Templates`,
		RequiredDocument: (dealGuid: Guid | undefined) => `Api/Deals/${dealGuid}/RequiredDocuments`,
		Reorder: (dealGuid: Guid | undefined) => `Api/Deals/${dealGuid}/RequiredDocuments/Reorder`,
		Download: (dealGuid: Guid | undefined, documentGuid: Guid | undefined, fileGuid: Guid | undefined) =>
			`Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}/Files/${fileGuid}`,
		AbsoluteDownload: (dealGuid: Guid | undefined, documentGuid: Guid | undefined, fileGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}/Files/${fileGuid}`,
		Upload: (dealGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}/Files`,
		UploadExample: (dealGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}/TemplateFile`,
		SpecificFile: (dealGuid: Guid | undefined, documentGuid: Guid | undefined, fileGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/RequiredDocuments/${documentGuid}/Files/${fileGuid}`,
	},

	Matching: {
		Get: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}`,

		Stats: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Stats`,

		Tranches: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Tranches`,

		DealTypePreferences: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/DealTypePreferences`,

		OutputAnalysis: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/OutputAnalysis`,
		OutputAnalysisHeader: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/OutputAnalysis/Header`,
		OutputAnalysisItem: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/OutputAnalysis/Item`,

		Params: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Params`,

		Processing: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/IsProcessing`,

		LoanOptionOverride: (dealGuid: Guid | undefined, optionGuid: Guid | undefined) =>
			`/Api/Matching/${dealGuid}/DealTypePreferences/${optionGuid}`,

		Rates: () => `/Api/Matching/CurrentRates`,

		Lenders: (
			dealGuid?: string,
			mode?: number,
			tier1?: number,
			finderFee?: boolean,
			structure?: number | null,
			filterType?: number,
			matchMode?: number | null
		) =>
			structure != null
				? `/Api/Matching/${dealGuid}/Lenders?mode=${mode}&matchMode=${
						matchMode ?? 1
				  }&tier1=${tier1}&finderFee=${finderFee}&structure=${structure}&filterType=${filterType}`
				: `/Api/Matching/${dealGuid}/Lenders?mode=${mode}&matchMode=${
						matchMode ?? 1
				  }&tier1=${tier1}&finderFee=${finderFee}&filterType=${filterType}`,

		TermSheets: (dealGuid?: string, structure?: number | null, tier1?: number | null) =>
			`/Api/Matching/${dealGuid}/TermSheets?structure=${structure}&tier1=${tier1}`,

		Search: (
			dealGuid?: string,
			keywords?: string,
			tier1?: number | null,
			tier3?: number | null,
			mode?: number | null,
			geographyMode?: number | null
		) =>
			`/Api/Matching/${dealGuid}/Search?keywords=${keywords}&mode=${
				mode ?? 1
			}&geographyMode=${geographyMode}&tier1=${tier1 ?? ''}&tier3=${tier3 ?? ''}`,
		UnavailableReasons: (dealGuid?: string, structureTranche?: number | null, tier1BankType?: number | null) =>
			`/Api/Matching/${dealGuid}/Unavailable?structure=${structureTranche}&tier1=${tier1BankType}`,
	},

	LenderList: {
		Root: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Deals/${dealGuid}/LenderList?structure=${structure}`,
	},

	ParticipantList: {
		Root: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Deals/${dealGuid}/LenderParticipant?structure=${structure}`,
	},

	PreMarketEmail: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/PreMarketEmails`,
	},

	Lookups: {
		Industries: '/Api/Industries',
		FinancialCovenantTypes: `/Api/FinancialCovenantTypes`,
		States: (search?: string | null) => (search == null ? `/Api/States` : `/Api/States?search=${search}`),
		NaicsCodes: (search?: string | null) => (search == null ? `Api/NaicsCodes` : `Api/NaicsCodes?search=${search}`),
		ExplorerProducts: () => `/Api/Products/CreditExplorer`,
		NavigatorProducts: () => `/Api/Products/ComplianceNavigator`,
		TermSheetFees: `/Api/TermSheetFees`,
		BankPartials: (search?: string | null, companyGuid?: Guid | undefined) => {
			let url = 'Api/Banks/Partials?';

			if (search) url += `search=${search}`;
			if (companyGuid) url += `&companyGuid=${companyGuid}`;

			return url;
		},
		ConditionTypes: `/Api/ConditionTypes`,
		ApplicationSettings: `/Api/Settings/All`,
	},

	Company: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Company`,
		SuccessFeeTiers: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/SuccessFeeTiers`,
		SepcificSuccessFeeTiers: (
			companyGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			tierGuid: Guid | undefined
		) => `/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/SuccessFeeTiers/${tierGuid}`,
		PaymentTerms: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/PaymentTerms`,
		SepcificPaymentTerm: (
			companyGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			paymentTermGuid: Guid | undefined
		) => `/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/PaymentTerms/${paymentTermGuid}`,
		SpecificCompanySubscription: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}`,
		UploadSubscriptionAgreement: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/Agreement`,
		SpecificSubscriptionDownload: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/Download`,
		SubscriptionContractUpload: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/UploadContract`,
		SendContractForSigning: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/Contract/SendForSigning`,
		UpdateContractReminder: (
			companyGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			agreementId: string | undefined
		) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/Agreement/${agreementId}/Reminders`,
		AgreementStatus: (
			companyGuid: Guid | undefined,
			subscriptionGuid: Guid | undefined,
			agreementId: string | undefined
		) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscriptionGuid}/Contract/${agreementId}/status`,
	},
	ActiveLenders: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ActiveLenders`,
	FinderFees: {
		ByLender: (lenderGuid: Guid | undefined | null) => `/Api/Banks/FinderFees/${lenderGuid}`,
	},
	TermSheets: {
		Add: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ExistingDebtTerms/Add`,
		Active: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ActiveTermSheets`,
		ExistingTerms: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ExistingDebtTerms`,
		Specific: (dealGuid: Guid | undefined, termSheetGuid: Guid | null | undefined) =>
			`/Api/Deals/${dealGuid}/ExistingDebtTerms/${termSheetGuid}`,
		Revision: (
			dealGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/Deals/${dealGuid}/ExistingDebtTerms/${termSheetGuid}/Revisions/${revisionGuid}`,
		RevisionMatchingParams: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/DealMatchingParams`,
		ReplaceFile: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/ReplaceFile`,
		AbsoluteDownload: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | undefined,
			revisionGuid: Guid | undefined
		) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Download`,
		Facility: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility`,
		FacilitySpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}`,
		PainPoint: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/PainPoint`,
		PainPointSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			painPointGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/PainPoint/${painPointGuid}`,
		FacilityFees: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Fees`,
		SpecificFacilityFee: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			feeGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Fees/${feeGuid}`,
		FacilityCollateral: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Collateral`,
		SpecificFacilityCollateral: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			collateralGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Collateral/${collateralGuid}`,
		Covenant: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Covenant`,
		CovenantSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			covenantGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Covenant/${covenantGuid}`,
		Reporting: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Reporting`,
		ReportingSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			reportingGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Reporting/${reportingGuid}`,
		Conditions: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Conditions`,
		ConditionSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			conditionGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Conditions/${conditionGuid}`,
		HistoricalBankRate: (revisionGuid: Guid | null | undefined, rateType: BaseRateTypes | null | undefined) =>
			`/Api/DataRooms/GetBankRateHistoricalValue?revisionGuid=${revisionGuid}&bankRateType=${rateType}`,
	},
	CompanyContacts: {
		Root: (companyGuid: Guid | undefined) => `/Api/CompanyContacts/${companyGuid}`,
		BorrowerParticipants: (dataRoomGuid: Guid | undefined) => `/Api/CompanyContacts/DataRoom/${dataRoomGuid}`,
		AddContactAndDataRoomAccess: (companyGuid: Guid | undefined, dataRoomGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/AddContactAndDataRoomAccess/${dataRoomGuid}`,
		AddExistingDataRoomAccess: (companyGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/AddExistingDataRoomAccess`,
		UpdateExistingDataRoomAccess: (companyGuid: Guid | undefined, contactGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/UpdateExistingDataRoomAccess/${contactGuid}`,
		UpdateLastAccess: (dealGuid: Guid | undefined, personId: number | undefined) =>
			`/Api/CompanyContacts/${dealGuid}/UpdateLastAccessed/${personId}`,
		PasswordResetLink: (companyGuid: Guid | undefined, contactGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/GetPasswordResetLink/${contactGuid}`,
	},
	CompanyNdas: {
		Root: (companyGuid: Guid | undefined) => `/Api/Companies/${companyGuid}/Ndas/`,
		Upload: (companyGuid: Guid | undefined) => `/Api/Companies/${companyGuid}/Ndas/Upload`,
		Active: (companyGuid: Guid | undefined) => `/Api/Companies/${companyGuid}/Ndas/Active`,

		Download: (companyGuid: Guid | undefined, ndaGuid: Guid | undefined) =>
			`/Api/Companies/${companyGuid}/ndas/${ndaGuid}/Download`,
		AbsoluteDownload: (companyGuid: Guid | undefined, ndaGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/Companies/${companyGuid}/ndas/${ndaGuid}/Download`,

		Blank: `/Api/Companies/Ndas/Blank`,
		AbsoluteBlank: `${ApiRootUrl}/Api/Companies/Ndas/Blank`,
	},

	Questions: {
		LoanApp: '/api/Questions?includeLoanAppQuestions=true',
		Answers: (dealGuid: Guid | undefined) => `/api/Deals/${dealGuid}/QuestionAnswers`,
	},

	Products: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Product`,

	DefaultProduct: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Product/Default`,

	Integrations: {
		Config: `/Api/Integrations/Config`,
	},

	/**
	 * Builds an absolute version of an endpoint url
	 * @param endpoint An endpoint url string, preferably from this same object.
	 * @returns The endpoint string concatenated after the ApiRootUrll
	 */
	Absolute: (endpoint: string) => `${ApiRootUrl}${endpoint}`,
};

export function toQueryParams(paramName: string, values: string[]) {
	const queryParamKey = `&${paramName}=`;
	return queryParamKey + values.join(queryParamKey);
}
